<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { buttonProps } from 'naive-ui';
import { gaiaFontWeight, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  ...buttonProps,
  /** primary = answer follow-ups (radius 8), secondary = composer suggestions (pill). */
  variant: {
    type: String as PropType<'primary' | 'secondary'>,
    default: 'primary',
  },
});

// `variant` is ours, not n-button's — keep it off the DOM element.
const passThrough = computed(() => {
  const { variant, ...rest } = props;
  return rest;
});

// Chips are outline-only: solid backgrounds are reserved for primary buttons.
// Colors come from the shared tokens; only the variant shapes live here.
// Caller-supplied theme-overrides win (spread last).
const mergedThemeOverrides = computed(() => ({
  fontSizeTiny: '11px',
  fontSizeSmall: '12px',
  fontSizeMedium: '14px',
  fontSizeLarge: '16px',
  fontWeight: String(gaiaFontWeight.regular),
  color: gaiaNeutral.surface,
  textColor: gaiaNeutral.text,
  border: `1px solid ${gaiaNeutral.border}`,
  borderHover: `1px solid ${gaiaNeutral.ink}`,
  borderPressed: `1px solid ${gaiaNeutral.ink}`,
  borderFocus: `1px solid ${gaiaNeutral.ink}`,
  textColorHover: gaiaNeutral.ink,
  textColorPressed: gaiaNeutral.ink,
  textColorFocus: gaiaNeutral.ink,
  rippleColor: gaiaNeutral.ink,
  ...(props.variant === 'secondary'
    ? {
        borderRadiusTiny: '20px',
        borderRadiusSmall: '20px',
        borderRadiusMedium: '20px',
        borderRadiusLarge: '20px',
        heightMedium: '32px',
        paddingMedium: '0 14px',
        colorHover: gaiaNeutral.surfaceTint,
        colorPressed: gaiaNeutral.surfaceTint,
        colorFocus: gaiaNeutral.surfaceTint,
      }
    : {
        borderRadiusTiny: '8px',
        borderRadiusSmall: '8px',
        borderRadiusMedium: '8px',
        borderRadiusLarge: '8px',
        heightMedium: '34px',
        paddingMedium: '0 14px',
        colorHover: gaiaNeutral.surface,
        colorPressed: gaiaNeutral.surface,
        colorFocus: gaiaNeutral.surface,
      }),
  ...props.themeOverrides,
}));
</script>

<template>
  <n-button v-bind="passThrough" :theme-overrides="mergedThemeOverrides">
    <slot />
  </n-button>
</template>
